/**
 * Subs' date moves waiting on the PM, at the top of the Schedule tab (Duncan 2026-10-24): a sub moves its task's dates
 * on E3 Subs and starts work -- "we don't want to block the subcontractor from rescheduling the tasks and starting to
 * work" -- and the PM is "final approval for that schedule ... because it involves billing". Summit's dates are what
 * bill (a task's progress is capped by its own dates), so the answer decides what can be billed.
 *
 * Accept shows where Summit's schedule lands the task and what moves with it, then sets the dates as a PM's own edit
 * does (updateTask: its pin, cascade, schedule log and feed) and tells E3 Subs. Decline keeps Summit's dates, with a
 * note for the sub. Words as E3 Subs' (its b76b123).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui';
import { ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import { answerMove, previewMove, updateTask } from '../../api';

const newRequestId = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); }));

/** A calendar day ("2027-02-01"), read as that day here (never UTC midnight). */
const dayOf = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const md = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
/** "Feb 1 – Feb 20", with the year when it isn't this one: "Feb 1 – Feb 20, 2027", "Dec 28, 2026 – Jan 8, 2027". */
export const fmtRange = (start, finish) => {
  const s = dayOf(start);
  const f = dayOf(finish);
  if (!s || !f) return '—';
  const now = new Date().getFullYear();
  if (s.getFullYear() !== f.getFullYear()) return `${md(s)}, ${s.getFullYear()} – ${md(f)}, ${f.getFullYear()}`;
  return `${md(s)} – ${md(f)}${s.getFullYear() !== now ? `, ${s.getFullYear()}` : ''}`;
};
/** "2 h ago", "3 days ago". */
const ago = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const muted = { fontSize: 13, color: 'var(--text-3)' };
const smallBtn = { height: 30, fontSize: 13, padding: '0 12px' };

function MoveCard({ move, code, focused, onAnswered, onTaskChanged }) {
  const [mode, setMode] = useState(null); // null | 'accept' | 'decline'
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (focused && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focused]);

  // Taken back, moved again, answered elsewhere or unknown: the list is out of date.
  const gone = (e) => e && (e.code === 'MOVE_GONE' || e.code === 'ALREADY_ANSWERED' || (e.status === 404 && /move/.test(e.message || '')));

  const openAccept = async () => {
    setMode('accept'); setError(null); setPreview(null); setBusy('preview');
    try {
      setPreview(await previewMove(move.taskId, move.id));
    } catch (e) {
      setError(e.message || 'Couldn’t work out the move.');
      if (gone(e)) onAnswered();
    } finally {
      setBusy(null);
    }
  };

  const accept = async () => {
    setBusy('accept'); setError(null);
    try {
      // E3 Subs again, just before saving: the sub may have moved it since this was opened.
      const now = await previewMove(move.taskId, move.id);
      if (!preview || now.move.start !== preview.move.start || now.move.finish !== preview.move.finish) {
        setPreview(now);
        setError('The sub changed the dates since you opened this. Look again.');
        return;
      }
      await updateTask(move.taskId, { start_date: now.move.start, finish_date: now.move.finish });
      onTaskChanged();
      await answerMove(move.taskId, move.id, { answer: 'ACCEPTED', requestId: newRequestId() });
      onAnswered();
    } catch (e) {
      setError(e.message || 'Couldn’t accept the move.');
      if (gone(e)) onAnswered();
    } finally {
      setBusy(null);
    }
  };

  const decline = async () => {
    setBusy('decline'); setError(null);
    try {
      await answerMove(move.taskId, move.id, { answer: 'DECLINED', note: note.trim() || undefined, requestId: newRequestId() });
      onAnswered();
    } catch (e) {
      setError(e.message || 'Couldn’t decline the move.');
      if (gone(e)) onAnswered();
    } finally {
      setBusy(null);
    }
  };

  const close = () => { setMode(null); setError(null); setPreview(null); setNote(''); };
  const lands = preview && (preview.start_date !== preview.move.start || preview.finish_date !== preview.move.finish);

  return (
    <div ref={ref} style={{
      background: 'var(--bg-surface)', borderRadius: 9, padding: '9px 12px',
      border: focused ? '1.5px solid var(--warning)' : '1px solid var(--border-subtle)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: 500 }}>
            <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12.5, marginRight: 6 }}>{code || move.task.code}</span>
            {move.task.name}
            {move.task.location && <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {move.task.location}</span>}
          </div>
          <div style={{ ...muted, marginTop: 2 }}>
            {move.sub || 'The sub'} moved it to <b style={{ color: 'var(--text-1)', fontWeight: 600 }}>{fmtRange(move.start, move.finish)}</b>
            {' '}(yours: {fmtRange(move.task.start, move.task.finish)}){move.by ? ` · ${move.by}` : ''}{move.at ? `, ${ago(move.at)}` : ''}
          </div>
          {move.note && <div style={{ ...muted, marginTop: 2, fontStyle: 'italic' }}>“{move.note}”</div>}
          {(move.cascade || []).length > 0 && (
            <div style={{ ...muted, marginTop: 2 }}>
              Moved along with it: {move.cascade.map((c, i) => (
                <span key={c.taskId}>{i ? ' · ' : ''}<span className="mono" style={{ fontSize: 12 }}>{c.code}</span> {fmtRange(c.start, c.finish)}</span>
              ))}
            </div>
          )}
          {/* What accepting moves in Summit's schedule, before anyone clicks (Duncan 2026-10-28: "There's nothing that shows
              us what that does to the rest of the dates if it has tasks that are dependent on it"). */}
          {Array.isArray(move.knock) && (move.knock.length ? (
            <div style={{
              marginTop: 7, padding: '7px 10px', borderRadius: 8,
              background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-text)' }}>
                Accepting moves {move.knock.length} later task{move.knock.length === 1 ? '' : 's'} with it
              </div>
              {move.knock.slice(0, 6).map((t) => (
                <div key={t.id} style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>
                  <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12, marginRight: 6 }}>{t.task_code}</span>
                  {t.task_name}
                  {t.after && <span style={{ color: 'var(--text-3)' }}> · after {Number(t.after.id) === Number(move.taskId) ? 'this one' : t.after.task_name}</span>}
                  <span style={{ color: 'var(--text-3)', textDecoration: 'line-through', margin: '0 6px' }}>{fmtRange(t.from_start, t.from_finish)}</span>
                  <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{fmtRange(t.start_date, t.finish_date)}</span>
                </div>
              ))}
              {move.knock.length > 6 && <div style={{ ...muted, marginTop: 3 }}>and {move.knock.length - 6} more</div>}
            </div>
          ) : (
            <div style={{ ...muted, marginTop: 4 }}>Nothing else in the schedule moves with it.</div>
          ))}
        </div>
        {!mode && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <SecondaryButton onClick={openAccept} style={smallBtn}><Icon name="check" size={13} />Accept</SecondaryButton>
            <SecondaryButton onClick={() => { setMode('decline'); setError(null); }} style={smallBtn}>Decline</SecondaryButton>
          </span>
        )}
      </div>

      {mode === 'accept' && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
          {busy === 'preview' && <div style={muted}>Working out what moves…</div>}
          {preview && (
            <>
              <div style={{ fontSize: 13.5, color: 'var(--text-1)' }}>These become the job’s dates, and billing follows them.</div>
              {lands && (
                <div style={{ ...muted, marginTop: 4 }}>
                  Summit’s schedule puts it at <b style={{ color: 'var(--text-1)', fontWeight: 600 }}>{fmtRange(preview.start_date, preview.finish_date)}</b> (its links and working days).
                </div>
              )}
              {preview.moved.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={muted}>Linked tasks move with it:</div>
                  {preview.moved.map((t) => (
                    <div key={t.id} style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 2 }}>
                      <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12, marginRight: 6 }}>{t.task_code}</span>
                      {t.task_name} <span style={{ color: 'var(--text-3)' }}>{fmtRange(t.from_start, t.from_finish)} →</span> {fmtRange(t.start_date, t.finish_date)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {error && <div style={{ marginTop: 8 }}><ErrorBanner>{error}</ErrorBanner></div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <PrimaryButton busy={busy === 'accept'} disabled={!preview || busy === 'preview'} onClick={accept} style={smallBtn}>Accept move</PrimaryButton>
            <SecondaryButton onClick={close} style={smallBtn}>Cancel</SecondaryButton>
          </div>
        </div>
      )}

      {mode === 'decline' && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: 13.5, color: 'var(--text-1)' }}>Summit’s dates stay. Work the sub does outside them can’t be billed until then.</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} autoFocus
            placeholder="Note for the sub (optional)" aria-label="Note for the sub"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border-default)',
              background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical' }} />
          {error && <div style={{ marginTop: 8 }}><ErrorBanner>{error}</ErrorBanner></div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <PrimaryButton busy={busy === 'decline'} onClick={decline} style={smallBtn}>Keep my dates</PrimaryButton>
            <SecondaryButton onClick={close} style={smallBtn}>Cancel</SecondaryButton>
          </div>
        </div>
      )}
    </div>
  );
}

/** The amber banner: every move waiting on the PM on this job, oldest first. Nothing when there are none. */
export default function SubMovesBanner({ moves, codeOf, focusId, onAnswered, onTaskChanged }) {
  if (!moves || !moves.length) return null;
  return (
    <div id="sub-moves" style={{
      marginBottom: 14, padding: '10px 12px', borderRadius: 12,
      background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14.5, fontWeight: 600, color: 'var(--warning)' }}>
          <Icon name="calendar" size={15} />
          {moves.length} date move{moves.length === 1 ? '' : 's'} waiting on you
        </span>
        <span style={muted}>Billing follows Summit’s dates. Until you accept, work outside them can’t be billed.</span>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {moves.map((m) => (
          <MoveCard key={m.id} move={m} code={codeOf ? codeOf(m.taskId) : null} focused={focusId === m.id}
            onAnswered={onAnswered} onTaskChanged={onTaskChanged} />
        ))}
      </div>
    </div>
  );
}
